import {
  InsertComponentRequestData,
  ContainerConfiguration,
  DataRequestCallback,
  ComponentLoaderData,
  Audience,
  DiscoverData,
  DiscoverDataRequest,
  DiscoverLightDismissData,
  QueryableData,
  DiscoverDataEntry,
  MenuViewProvider,
  ComponentDataType,
  MenuViewCloseSource,
  MenuViewItem,
  MenuViewProps,
  MenuRenderProps,
  SettingsProvider,
  BlueprintInsertionData
} from '@fluidx/office-fluid-types';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import {
  sendErrorEvent,
  sendUserActionEvent,
  getFluidExperiencesSetting,
  enableUpToAudience,
  defaultLocale,
  resolveLocale,
  sendTelemetryEvent
} from '@fluidx/utilities';
import { UserAction, ErrorEvent } from '../telemetry';
import {
  isComponentData,
  isActivityBlueprintData,
  isContainerManifestEntry,
  isBlueprintData
} from './DiscoverDataHelpers';
import { getStringTable } from '../LocalizationUtilities';

type RenderDiscoverMenuProps = Readonly<{
  insertRequestData: InsertComponentRequestData;
  producerCallback: DataRequestCallback;
  logger?: ITelemetryBaseLogger;
  audience?: Audience;
  locale?: string;
  menuViewProvider?: MenuViewProvider;
  settingsProvider?: SettingsProvider;
}>;

/**
 * If a menu is currently displayed, retains a reference to its state. If no
 * menu is displayed, is undefined.
 */
let currentDiscoverMenu: DiscoverMenu | undefined;

/**
 * Represents the menu being displayed
 */
type DiscoverMenu = Readonly<{
  /**
   * Updates the existing menu with a new set of props which may update its
   * display or internal state.
   */
  update(props: RenderDiscoverMenuProps): Promise<void>;
}>;

/**
 * Render Discover Menu UX.
 */
export async function renderDiscoverMenu(props: RenderDiscoverMenuProps) {
  if (currentDiscoverMenu === undefined) {
    currentDiscoverMenu = await createDiscoverMenu(props);
  }

  await currentDiscoverMenu?.update(props);
}

/**
 * Creates a new state closure for a menu. Lasts until the menu is dismissed.
 */
async function createDiscoverMenu(props: RenderDiscoverMenuProps): Promise<DiscoverMenu | undefined> {
  const { locale = defaultLocale, menuViewProvider } = props;

  if (menuViewProvider === undefined) {
    return undefined;
  }

  const resolvedLocale = resolveLocale(locale);
  const stringTable = await getStringTable(resolvedLocale);

  // The set of state variables which should remain across all calls to
  // renderDiscoverMenu before it's cleaned up. Simulates behavior of a React
  // state variables.
  let isDismissed: boolean = false;
  let searchQueryHash: number = 0;
  let inputLengthOnLastSuggestion: number = 0;

  let searchResults: DiscoverData | undefined;
  let currentSearchResults: DiscoverData | undefined;
  let searchQuery: string = '';
  let updateSearchResultsTimeout: NodeJS.Timeout | undefined;
  let announceSearchResultsTimeout: NodeJS.Timeout | undefined;

  // Stores the last seen query text value. If it changes between updates, we
  // need to update our searchQuery.
  let previousQueryText: string | undefined;

  return {
    async update(props: RenderDiscoverMenuProps) {
      const { insertRequestData, producerCallback, logger, audience } = props;
      const { floatingUXContext } = insertRequestData.hostContext;

      const rendererProps: MenuViewProps = {
        ...props,
        stringTable,
        coordinates: floatingUXContext?.coordinates,
        coordinatesFlip: floatingUXContext?.coordinatesFlip,
        ownerDocument: floatingUXContext?.ownerDocument
      };

      const renderer = await menuViewProvider.getView(rendererProps);
      if (renderer === undefined) {
        return;
      }

      // If we are in a container, we expect to insert components so the componentContext should be defined.
      const insertionContext = insertRequestData.insertionContext;
      if (insertionContext !== undefined) {
        const {
          onInsert,
          onInsertActivityBlueprint,
          onInsertComponent,
          onInsertBlueprint,
          onClose,
          queryText
        } = insertionContext;

        const insert = (containerConfiguration: ContainerConfiguration) => {
          if (onInsert === undefined) {
            return;
          }
          if (logger) {
            switch (containerConfiguration.configurationType) {
              case 'componentConfiguration':
                sendUserActionEvent(logger, {
                  eventName: UserAction.InsertComponentFromDiscover,
                  componentType: containerConfiguration.loadComponentData.componentType?.toString()
                });
                break;

              case 'templateConfiguration':
                sendUserActionEvent(logger, {
                  eventName: UserAction.InsertActivityBlueprintFromDiscover,
                  configurationId: containerConfiguration.loadTemplateData.configurationId?.toString()
                });
                break;

              default:
                // Should we log something here?
                break;
            }
          }

          try {
            onInsert(containerConfiguration);
          } catch (error: any) {
            if (logger) {
              switch (containerConfiguration.configurationType) {
                case 'componentConfiguration':
                  logger &&
                    sendErrorEvent(
                      logger,
                      {
                        eventName: ErrorEvent.InsertComponentFromDiscoverError,
                        componentType: containerConfiguration.loadComponentData.componentType?.toString()
                      },
                      error
                    );
                  break;

                case 'templateConfiguration':
                  logger &&
                    sendErrorEvent(
                      logger,
                      {
                        eventName: ErrorEvent.InsertActivityBlueprintFromDiscoverError,
                        configurationId: containerConfiguration.loadTemplateData.configurationId?.toString()
                      },
                      error
                    );
                  break;

                default:
                  // Should we log something here?
                  break;
              }
            }
          }
        };

        const insertComponent = (componentLoaderData: ComponentLoaderData) => {
          if (onInsertComponent === undefined) {
            return;
          }
          if (logger) {
            sendUserActionEvent(logger, {
              eventName: UserAction.InsertComponentFromDiscover,
              componentType: componentLoaderData.componentType?.toString()
            });
          }
          try {
            onInsertComponent(componentLoaderData);
          } catch (error: any) {
            if (logger) {
              sendErrorEvent(
                logger,
                {
                  eventName: ErrorEvent.InsertComponentFromDiscoverError,
                  componentType: componentLoaderData.componentType?.toString()
                },
                error
              );
            }
          }
        };

        const insertBlueprint = (blueprintInsertionData: BlueprintInsertionData) => {
          if (onInsertBlueprint === undefined) {
            return;
          }
          if (logger) {
            sendUserActionEvent(logger, {
              eventName: UserAction.InsertBlueprintFromDiscover,
              blueprintName: blueprintInsertionData.blueprintName
            });
          }
          try {
            onInsertBlueprint(blueprintInsertionData);
          } catch (error: any) {
            if (logger) {
              sendErrorEvent(
                logger,
                {
                  eventName: ErrorEvent.InsertComponentFromDiscoverError,
                  blueprintName: blueprintInsertionData.blueprintName
                },
                error
              );
            }
          }
        };

        const commitSuggestion = (data: DiscoverDataEntry) => {
          if (isComponentData(data)) {
            if (insertComponent) {
              insertComponent(data.loaderData);
            } else if (insert) {
              insert({
                configurationType: 'componentConfiguration',
                loadComponentData: data.loaderData
              });
            }
          } else if (isBlueprintData(data)) {
            if (insertBlueprint) {
              insertBlueprint(data.blueprintData);
            }
          } else if (isActivityBlueprintData(data)) {
            if (insert) {
              insert(data.configuration);
            }
          } else if (isContainerManifestEntry(data)) {
            if (onInsertActivityBlueprint) {
              onInsertActivityBlueprint(data);
            }
          }
        };

        const closeSourceToFlags = (source: MenuViewCloseSource): CloseFlags => {
          switch (source) {
            case 'searchBackspace':
              return { restoreFocus: true, dismissed: true };
            case 'searchEscape':
            case 'calloutEscape':
              return { replaceWithExistingContent: true, restoreFocus: true, dismissed: true };
            case 'executeItem':
              return { restoreFocus: true };
            case 'calloutDismiss':
              return { replaceWithExistingContent: (queryText && queryText()) !== undefined, dismissed: true };
          }
        };

        const close = (source: MenuViewCloseSource, existingContent: string) => {
          const { replaceWithExistingContent = false, restoreFocus = false, dismissed = false } = closeSourceToFlags(
            source
          );

          dismissed &&
            logger &&
            sendUserActionEvent(logger, { eventName: UserAction.CloseDiscoverWithoutContentInserted });

          // Only replace with existing content if Discover decides to insert content and the invoking component
          // isn't preventing the content insertion.
          const preventContentInsertion: boolean = insertRequestData.hostContext.preventContentInsertion || false;
          onClose && onClose(!preventContentInsertion && replaceWithExistingContent, existingContent, restoreFocus);

          // Clean up the current menu state
          renderer.clear();
          currentDiscoverMenu = undefined;
        };

        const {
          canHandleInlineComponentsOnly: showInlineComponentsOnly,
          permittedComponentDataTypes,
          disallowedComponentDataTypes,
          contextData,
          showSearch
        } = insertRequestData.hostContext;

        // VSO (5326573): We need to check what's going on on some hosts
        const logDiscoverInlineTelemetry = getFluidExperiencesSetting(
          'discover.inline.telemetry' /* settingName */,
          false /* defaultValue */,
          undefined /* settingsProvider */
        );
        if (logDiscoverInlineTelemetry && logger) {
          sendTelemetryEvent(logger, {
            eventName: 'DiscoverInlineTelemetry',
            logPoint: 'renderDiscoverMenu.update',
            showInlineComponentsOnly,
            permittedComponentDataTypes: JSON.stringify(permittedComponentDataTypes),
            disallowedComponentDataTypes: JSON.stringify(disallowedComponentDataTypes)
          });
        }

        const {
          lightDismissEnabled,
          shouldLightDismiss,
          announceSearchResults,
          transferFocus: shouldRequestFocus,
          insertFirstComponent,
          shouldForceClose,
          restoreFocusAndUpdateQuery
        } = insertRequestData.insertionContext || {};

        /**
         * Updates the list of search results by querying every component subscribed to
         * this search query for new available actions.
         */
        const updateSearchResults = (searchQuery: string) => {
          // We have received Dogfood complaints that invoking the menu on + is invasive.
          // To alleviate these issues, if the first thing the user types is a space, we
          // assume they didn't intend to open the menu, close it, and replace it with
          // the space character they typed.
          if (searchQuery === ' ') {
            close('calloutEscape', searchQuery);
            return;
          }

          currentSearchResults = undefined;

          searchQueryHash += 1;
          const targetSearchQueryHash = searchQueryHash;

          const request: DiscoverDataRequest = {
            setDiscoverData: (discoverData: DiscoverData) => {
              // Only count the results as valid if the hash on request
              // is the same when we receive the response. This prevents us
              // from populating the menu with results that don't match the
              // current search query.
              if (targetSearchQueryHash === searchQueryHash) {
                setDiscoverData(discoverData, searchQuery, setSearchResults);
              }
            },
            searchQuery
          };

          producerCallback('DiscoverData', request);
        };

        const setDiscoverData = (
          discoverData: DiscoverData,
          searchQuery: string,
          setSearchResults: (results: DiscoverData | undefined) => void
        ) => {
          const newSearchResults: DiscoverData = filterData(discoverData, searchQuery);

          const newResults = (currentSearchResults ? currentSearchResults : []).concat(newSearchResults);
          if (contextData) {
            const filteredContextData = filterData(contextData, searchQuery);
            filteredContextData.filter((data) => !newResults.includes(data)).forEach((data) => newResults.push(data));
          }

          if (newResults) {
            const lightDismissData: DiscoverLightDismissData = {
              searchResultsCount: newResults.length,
              queryLength: searchQuery.length,
              inputLengthOnLastSuggestion
            };
            if (lightDismissEnabled && shouldLightDismiss && shouldLightDismiss(lightDismissData)) {
              close('calloutEscape', searchQuery);
            } else if (newResults.length > 0) {
              inputLengthOnLastSuggestion = searchQuery.length;
            }
          }

          if (
            announceSearchResults &&
            (currentSearchResults?.length !== newResults.length || newResults.length === 0) &&
            !shouldRequestFocus &&
            !announceSearchResultsTimeout
          ) {
            announceSearchResultsTimeout = setTimeout(() => {
              let additionalInfo = '';
              if (currentSearchResults && currentSearchResults.length > 0) {
                additionalInfo =
                  currentSearchResults[0].queryableData.displayName +
                  ' 1 of ' +
                  currentSearchResults.length +
                  ' selected';
                announceSearchResults(currentSearchResults.length, additionalInfo);
              }
            }, 1000);
          }
          currentSearchResults = newResults;
          setSearchResults(newResults);
        };

        const filterData = (discoverData: DiscoverData, searchQuery: string): DiscoverData => {
          let filteredData = discoverData.filter((data) => matchesSearchQuery(data.queryableData, searchQuery));

          // VSO (5326573): We need to check what's going on on some hosts
          const logDiscoverInlineTelemetry = getFluidExperiencesSetting(
            'discover.inline.telemetry' /* settingName */,
            false /* defaultValue */,
            undefined /* settingsProvider */
          );
          if (logDiscoverInlineTelemetry && logger) {
            sendTelemetryEvent(logger, {
              eventName: 'DiscoverInlineTelemetry',
              logPoint: 'renderDiscoverMenu.filterData callback',
              showInlineComponentsOnly,
              permittedComponentDataTypes: JSON.stringify(permittedComponentDataTypes),
              disallowedComponentDataTypes: JSON.stringify(disallowedComponentDataTypes)
            });
          }

          // Filter by inline components if specified.
          if (showInlineComponentsOnly) {
            filteredData = filteredData.filter((c) => c.queryableData.properties?.isInline);
          }

          if (permittedComponentDataTypes !== undefined || disallowedComponentDataTypes !== undefined) {
            filteredData = filteredData.filter((c) => {
              const componentDataTypes: ComponentDataType[] = [];
              c.queryableData.manifest?.forEach((c) => {
                if (c.dataType !== undefined) {
                  componentDataTypes.push(c.dataType);
                }
              });

              for (const dataType of componentDataTypes) {
                // If we find the type being declared as a disallowed type, we filter it out.
                if (disallowedComponentDataTypes && disallowedComponentDataTypes.indexOf(dataType) >= 0) {
                  return false;
                }

                // If permittedComponentDataTypes is defined and we find the type being declared as a permitted type, we allow it.
                if (permittedComponentDataTypes?.indexOf(dataType) !== -1) {
                  return true;
                }
              }
              return permittedComponentDataTypes === undefined;
            });
          }

          // TODO: Task 5117972: Using SettingsProvider in renderDiscoverMenu
          let testComponents = getFluidExperiencesSetting(
            'discover.testComponents' /* settingName */,
            false /* defaultValue */,
            undefined /* settingsProvider */
          );

          if (!testComponents) {
            filteredData = filteredData.filter((c) => !c.queryableData.properties?.testComponent);
          }

          // TODO: Task 5117972: Using SettingsProvider in renderDiscoverMenu
          let dogfoodComponents = getFluidExperiencesSetting(
            'discover.dogfoodComponents' /* settingName */,
            enableUpToAudience('Dogfood', audience) /* defaultValue */,
            undefined /* settingsProvider */
          );

          if (!dogfoodComponents) {
            filteredData = filteredData.filter((c) => !c.queryableData.properties?.dogfoodComponent);
          }

          return filteredData;
        };

        const setSearchQuery = (value: string) => {
          searchQuery = value;

          if (updateSearchResultsTimeout !== undefined) {
            clearTimeout(updateSearchResultsTimeout);
          }

          updateSearchResultsTimeout = setTimeout(() => {
            if (updateSearchResultsTimeout !== undefined) {
              clearTimeout(updateSearchResultsTimeout);
              updateSearchResultsTimeout = undefined;
            }

            updateSearchResults(searchQuery);
          }, 200);

          isDismissed = false;
        };

        /**
         * Close this component
         * @param replaceWithExistingContent
         * @param setFocus
         */
        const closeDiscover = (source: MenuViewCloseSource) => {
          if (isDismissed) {
            return;
          }

          isDismissed = true;

          close(source, searchQuery);
        };

        const shouldPreventDismiss = (continueEditing: boolean): boolean => {
          if (!isDismissed) {
            return continueEditing;
          }

          return true;
        };

        const setSearchResults = (results: DiscoverData | undefined): void => {
          searchResults = results;
          render();
        };

        const render = (): void => {
          const resultsAsItems: MenuViewItem[] | undefined = searchResults?.map((entry) => ({
            icon: entry.queryableData.icon,
            displayName: entry.queryableData.displayName,
            execute: () => commitSuggestion(entry)
          }));

          const renderProps: MenuRenderProps = {
            onClose: closeDiscover,
            showSearch,
            shouldRequestFocus,
            searchResults: resultsAsItems,
            searchQuery,
            setSearchQuery,
            restoreFocusAndUpdateQuery,
            shouldPreventDismiss
          };

          renderer.render(renderProps);
        };

        // Check to see if this menu is already dismissed. If so, don't trigger
        // any new activity.
        if (!isDismissed) {
          if (insertFirstComponent) {
            const firstMenuItem = searchResults && searchResults?.length > 0 ? searchResults[0] : undefined;
            if (firstMenuItem) {
              commitSuggestion(firstMenuItem);
              closeDiscover('executeItem');
            }
          } else {
            // If the given query changes, trigger a search result update
            const newQuery = (queryText && queryText()) || '';
            // This block triggers the update process based on the current state and
            // how it diverges from previous state
            if (newQuery !== previousQueryText) {
              searchQuery = newQuery;
              previousQueryText = newQuery;
              updateSearchResults(searchQuery);
            } else if (searchResults === undefined) {
              // If we haven't got any search results yet, make sure that we're
              // trying to update them.
              updateSearchResults(searchQuery);
            }

            if (shouldForceClose) {
              closeDiscover('calloutEscape');
            } else {
              // Ensure that we try to render the current state
              render();
            }
          }
        }
      }
    }
  };
}

type CloseFlags = Readonly<{
  replaceWithExistingContent?: boolean;
  restoreFocus?: boolean;
  dismissed?: boolean;
}>;

/**
 * Returns whether the given searchable item matches the given search query. If the
 * default name doesn't match, searches its keywords.
 */
function matchesSearchQuery(searchableItem: QueryableData, searchQuery: string): boolean {
  if (substringMatch(searchableItem.displayName, searchQuery)) {
    return true;
  }

  for (let i = 0; i < searchableItem.keywords.length; i = i + 1) {
    const keyword = searchableItem.keywords[i];
    if (substringMatch(keyword, searchQuery)) {
      return true;
    }
  }

  return false;
}

/**
 * Returns if b is a substring of a (inclusive).
 */
function substringMatch(a: string, b: string): boolean {
  const aSubstr = a.substr(/*from*/ 0, /*length*/ b.length);
  return searchCollator.compare(aSubstr, b) === 0;
}

/**
 * Collator used for comparing strings to filter suggestions.
 */
const searchCollator = new Intl.Collator(undefined, { usage: 'search', sensitivity: 'base' });
